import Header from 'app/components/header/Header';
import { PageBreakpointProvider } from 'app/components/layout/Breakpoint';
import getPageBreakpoint from 'app/components/layout/Page/getPageBreakpoint';
import { PAGE_BACKGROUND_COLOR } from 'app/constants/Colors';
import { firefoxPrintHackStyles } from 'app/constants/Styles';
import ReloadRequiredNotifier from 'app/containers/header/ReloadRequiredNotifier';
import { WithFontsForActiveLocale } from 'app/containers/layout/WithFontsForLanguage';
import { useCss } from 'app/utils/css';
import React from 'react';
import useMeasure from 'app/components/layout/useMeasure';
import { CallbackRef, useCombineCallbackRefs } from 'app/utils/react';
import DiscreetErrorBoundary from 'app/components/utilities/DiscreetErrorBoundary';
import ContentErrorBoundary from 'app/components/utilities/ContentErrorBoundary';

export enum PageVariant {
  Base = 'PAGE_BASE_STYLE',
  Wide = 'PAGE_WIDE_STYLE',
}

export const PAGE_BASE_STYLE = PageVariant.Base;
export const PAGE_WIDE_STYLE = PageVariant.Wide;

interface IProps {
  children?: React.ReactNode;
  variant?: PageVariant;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  getRef?: CallbackRef;
}

const PageLayout: React.SFC<IProps> = ({
  children,
  variant = PAGE_BASE_STYLE,
  header = <Header />,
  footer,
  getRef,
}) => {
  const css = useCss();
  const [size, measureRef] = useMeasure();
  const pageBreakpoint = getPageBreakpoint(size);

  const ref = useCombineCallbackRefs([getRef, measureRef]);

  return (
    <PageBreakpointProvider value={pageBreakpoint}>
      <WithFontsForActiveLocale
        className={css('page', {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexGrow: 1,
          background: PAGE_BACKGROUND_COLOR,
          '@media print': firefoxPrintHackStyles,
        })}
        getRef={ref}
        wrap="section"
      >
        <div
          className={css('fixedNotifications', {
            position: 'fixed',
            top: '20%',
            left: '10%',
            right: '10%',
            zIndex: 2,
          })}
        >
          <ReloadRequiredNotifier />
        </div>

        <DiscreetErrorBoundary name="PageLayoutHeader">
          <header
            key="header"
            className={css('headerContainer', {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              // Make anything coming out of header cover above main content
              width: '100%',
              zIndex: 1,
            })}
          >
            {header}
          </header>
        </DiscreetErrorBoundary>

        <ContentErrorBoundary>
          <div
            key="content"
            className={css('main', {
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'stretch',
              flexGrow: 1,
              margin: '0 auto',
              width: '100%',
              zIndex: 0,
              maxWidth: '1000px',
              '@media print': firefoxPrintHackStyles,

              ...(variant === PAGE_WIDE_STYLE
                ? {
                  maxWidth: '100%',
                  padding: '0',
                }
                : {
                  padding: '0 1em',
                }),
            })}
          >
            {children}
          </div>
        </ContentErrorBoundary>

        <DiscreetErrorBoundary name="PageLayoutFooter">
          <footer
            key="footer"
            className={css('footerContainer', {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              width: '100%',
            })}
          >
            {footer}
          </footer>
        </DiscreetErrorBoundary>
      </WithFontsForActiveLocale>
    </PageBreakpointProvider>
  );
};

export default PageLayout;
